import { memo, useCallback } from 'react';

import { Button } from '@/components/design-system/Button';
import type { CodeString } from '@/consts/codes';
import { type TranslationKey } from '@/i18n/i18n';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { cn } from '@/utils/cn';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FavoriteButtonProps {
  code: CodeString | undefined;
  itemText: TranslationKey;
  isActive?: boolean;
  isPopover?: boolean;
  className?: string;
}

export const FavoriteButton = memo(function FavoriteButton({
  code,
  itemText,
  isActive = false,
  isPopover = false,
  className
}: FavoriteButtonProps) {
  const { t } = useTranslation();
  const { isFavorite, toggleFavorite, isTogglingFavorite } = useFavoritesStore();

  const itemIsFavorite = isFavorite(code);

  const handleToggleFavorite = useCallback(() => {
    void toggleFavorite(code);
  }, [toggleFavorite, code]);

  return (
    <Button
      onClick={handleToggleFavorite}
      variant="text"
      size="icon"
      disabled={isTogglingFavorite}
      className={cn(
        isPopover ? 'h-auto w-auto p-0 hover:bg-transparent' : 'hover:bg-transparent',
        isActive
          ? '[&_svg]:!text-white'
          : itemIsFavorite
            ? '[&_svg]:!text-warning-400 hover:[&_svg]:!text-warning-500'
            : '[&_svg]:!text-white/40 hover:[&_svg]:!text-white/60',
        className
      )}
      aria-label={itemIsFavorite ? `Remove ${t(itemText)} from favorites` : `Add ${t(itemText)} to favorites`}
    >
      {itemIsFavorite ? <Star className="size-4 fill-current" /> : <Star className="size-4" />}
    </Button>
  );
});
